import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

export type ParentArticleListItem = {
  id: string;
  slug: string;
  title_ar: string;
  title_en: string;
  description_ar: string | null;
  description_en: string | null;
  cover_url: string | null;
  reading_minutes: number | null;
  published_at: string | null;
  tier: string;
  value_tags: string[];
};

export type ParentArticleFull = ParentArticleListItem & {
  body_ar: string | null;
  body_en: string | null;
};

// Public list of published parent articles (FREE only — paywalled excerpts could come later).
export const listPublicParentArticles = createServerFn({ method: "GET" }).handler(
  async (): Promise<ParentArticleListItem[]> => {
    const { data, error } = await supabaseAdmin
      .from("content_items")
      .select(
        "id,slug,title_ar,title_en,description_ar,description_en,cover_url,reading_minutes,published_at,tier,value_tags",
      )
      .eq("audience", "parent")
      .eq("status", "published")
      .eq("tier", "free")
      .not("slug", "is", null)
      .order("published_at", { ascending: false })
      .limit(100);
    if (error) {
      console.error("listPublicParentArticles", error);
      return [];
    }
    return (data ?? []) as ParentArticleListItem[];
  },
);

// Public read of one parent article by slug.
export const getPublicParentArticle = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z
      .object({
        slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<ParentArticleFull | null> => {
    const { data: row, error } = await supabaseAdmin
      .from("content_items")
      .select(
        "id,slug,title_ar,title_en,description_ar,description_en,cover_url,reading_minutes,published_at,tier,value_tags,body_ar,body_en",
      )
      .eq("audience", "parent")
      .eq("status", "published")
      .eq("tier", "free")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error || !row) return null;
    return row as ParentArticleFull;
  });
